import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PawPrint, Mail, Lock, User, Phone, ShieldCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const { login, register, verifyOtp, resendOtp, forgotPassword, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // View state
  const [viewMode, setViewMode] = useState('tabs'); // 'tabs', 'otp', 'forgot'

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register state
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPhoneError, setRegPhoneError] = useState('');

  // OTP state
  const [otpStep, setOtpStep] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtpSent, setForgotOtpSent] = useState(false);
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');

  const formatError = (detail) => {
    if (!detail) return 'Something went wrong';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
    return String(detail);
  };

  const startResendCooldown = () => {
    setResendCooldown(60);
    const t = setInterval(() => {
      setResendCooldown(prev => { if (prev <= 1) { clearInterval(t); return 0; } return prev - 1; });
    }, 1000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      const msg = formatError(err.response?.data?.detail);
      if (msg.includes('verify your email')) {
        setOtpEmail(loginEmail);
        setViewMode('otp');
      }
      setError(msg);
    } finally { setLoading(false); }
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setRegPhone(val);
    if (val.length > 0 && val.length < 10) setRegPhoneError('Enter a valid 10-digit number');
    else if (val.length === 10 && !/^[6-9]\d{9}$/.test(val)) setRegPhoneError('Must start with 6, 7, 8, or 9');
    else setRegPhoneError('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!regPhone) { toast.error('Mobile number is required'); return; }
    if (!/^[6-9]\d{9}$/.test(regPhone)) { toast.error('Enter a valid 10-digit mobile number'); return; }
    if (regPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(regName, regEmail, regPassword, regPhone ? `+91${regPhone}` : '');
      setOtpEmail(regEmail);
      setViewMode('otp');
      startResendCooldown();
      toast.success('Account created! Check your email for the OTP.');
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (otpValue.length !== 6) { toast.error('Enter the 6-digit OTP from your email'); return; }
    setLoading(true);
    try {
      await verifyOtp(otpEmail, otpValue);
      toast.success('Email verified! Please sign in.');
      setViewMode('tabs');
      setOtpValue('');
      setTab('login');
      setLoginEmail(otpEmail);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      await resendOtp(otpEmail);
      startResendCooldown();
      toast.success('OTP resent — check your email!');
    } catch { toast.error('Failed to resend OTP'); }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!forgotOtpSent) {
        await forgotPassword(forgotEmail);
        setForgotOtpSent(true);
        toast.success('OTP sent to your email.');
      } else {
        if (forgotOtp.length !== 6) { toast.error('Enter a 6-digit OTP'); setLoading(false); return; }
        if (forgotNewPassword.length < 6) { toast.error('Password must be at least 6 characters'); setLoading(false); return; }
        await resetPassword(forgotEmail, forgotOtp, forgotNewPassword);
        toast.success('Password reset successful! Please sign in.');
        setViewMode('tabs');
        setTab('login');
        setLoginEmail(forgotEmail);
        setForgotOtpSent(false);
        setForgotOtp('');
        setForgotEmail('');
        setForgotNewPassword('');
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-16" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <PawPrint className="w-10 h-10 text-rust mx-auto mb-3" />
          <h1 className="font-heading text-3xl font-bold text-dark-brown">Welcome to Pets Valley</h1>
          <p className="font-body text-sm text-light-brown mt-2">Sign in or create an account to start shopping</p>
        </div>

        <div className="bg-white rounded-2xl border border-border-custom p-6 shadow-sm">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2.5 mb-4 text-sm font-body" data-testid="auth-error">
              {error}
            </div>
          )}

          {viewMode === 'otp' ? (
            /* ── OTP Verification Screen ── */
            <div data-testid="otp-form">
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-full bg-rust/10 flex items-center justify-center mx-auto mb-3">
                  <ShieldCheck className="w-7 h-7 text-rust" />
                </div>
                <h3 className="font-heading text-xl font-bold text-dark-brown">Check Your Email</h3>
                <p className="font-body text-sm text-light-brown mt-1">
                  We sent a 6-digit OTP to
                </p>
                <p className="font-body text-sm font-semibold text-dark-brown mt-0.5">{otpEmail}</p>
              </div>

              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div>
                  <Label className="font-body text-sm text-dark-brown">Enter OTP</Label>
                  <Input
                    value={otpValue}
                    onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6-digit code"
                    inputMode="numeric"
                    maxLength={6}
                    className="mt-1 bg-cream/50 border-border-custom text-center text-2xl font-bold tracking-[0.5em] h-14"
                    data-testid="otp-input"
                    autoFocus
                  />
                  <p className="font-body text-xs text-light-brown text-center mt-1">OTP expires in 10 minutes</p>
                </div>
                <Button
                  type="submit"
                  disabled={loading || otpValue.length !== 6}
                  className="w-full bg-rust hover:bg-rust/90 text-cream font-body rounded-full h-11"
                  data-testid="verify-otp-btn"
                >
                  {loading ? <span className="spinner mr-2"></span> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  Verify OTP
                </Button>
              </form>

              <div className="mt-4 text-center">
                <p className="font-body text-sm text-light-brown mb-2">Didn't receive it? Check your spam folder or</p>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0}
                  className="inline-flex items-center gap-1.5 font-body text-sm text-rust hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="resend-otp-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                </button>
              </div>

              <button
                type="button"
                onClick={() => { setViewMode('tabs'); setOtpValue(''); setError(''); }}
                className="w-full mt-3 text-center font-body text-xs text-light-brown hover:text-dark-brown"
              >
                ← Back to Sign In
              </button>
            </div>
          ) : viewMode === 'forgot' ? (
            /* ── Forgot Password Screen ── */
            <div data-testid="forgot-auth">
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-full bg-rust/10 flex items-center justify-center mx-auto mb-3">
                  <Lock className="w-7 h-7 text-rust" />
                </div>
                <h3 className="font-heading text-xl font-bold text-dark-brown">Reset Password</h3>
                <p className="font-body text-sm text-light-brown mt-1">
                  {forgotOtpSent ? "Enter the OTP sent to your email and your new password." : "Enter your email to receive a password reset OTP."}
                </p>
              </div>

              <form onSubmit={handleForgotSubmit} className="space-y-4">
                {!forgotOtpSent ? (
                  <div>
                    <Label className="font-body text-sm text-dark-brown">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-brown" />
                      <Input value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} type="email" placeholder="you@example.com"
                        className="pl-10 bg-cream/50 border-border-custom" data-testid="forgot-email" required autoFocus />
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="font-body text-sm text-dark-brown">OTP</Label>
                      <Input
                        value={forgotOtp}
                        onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit PIN"
                        inputMode="numeric"
                        maxLength={6}
                        className="mt-1 bg-cream/50 border-border-custom font-body tracking-[0.2em]"
                        data-testid="forgot-otp"
                        required autoFocus
                      />
                    </div>
                    <div>
                      <Label className="font-body text-sm text-dark-brown">New Password</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-brown" />
                        <Input value={forgotNewPassword} onChange={(e) => setForgotNewPassword(e.target.value)} type="password" placeholder="Min 6 chars, 1 uppercase, 1 number"
                          className="pl-10 bg-cream/50 border-border-custom" data-testid="forgot-new-password" required />
                      </div>
                      <p className="font-body text-xs text-light-brown mt-1">Must be at least 6 characters, with 1 uppercase and 1 number.</p>
                    </div>
                  </>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-rust hover:bg-rust/90 text-cream font-body rounded-full h-11 mt-4"
                  data-testid={forgotOtpSent ? "reset-password-btn" : "send-reset-otp-btn"}
                >
                  {loading ? <span className="spinner mr-2"></span> : null}
                  {forgotOtpSent ? "Reset Password" : "Send OTP"}
                </Button>
              </form>

              <button
                type="button"
                onClick={() => { setViewMode('tabs'); setError(''); setForgotOtpSent(false); setForgotEmail(''); setForgotOtp(''); setForgotNewPassword(''); }}
                className="w-full mt-4 text-center font-body text-xs text-light-brown hover:text-dark-brown"
              >
                ← Back to Sign In
              </button>
            </div>
          ) : (
            /* ── Login / Register Tabs ── */
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-pale rounded-full p-1">
                <TabsTrigger value="login" className="rounded-full font-body text-sm data-[state=active]:bg-rust data-[state=active]:text-cream" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" className="rounded-full font-body text-sm data-[state=active]:bg-rust data-[state=active]:text-cream" data-testid="register-tab">Sign Up</TabsTrigger>
              </TabsList>

              {/* ── Sign In ── */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} data-testid="login-form">
                  <div className="space-y-4">
                    <div>
                      <Label className="font-body text-sm text-dark-brown">Email</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-brown" />
                        <Input value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} type="email" placeholder="you@example.com"
                          className="pl-10 bg-cream/50 border-border-custom" data-testid="login-email" required />
                      </div>
                    </div>
                    <div>
                      <Label className="font-body text-sm text-dark-brown">Password</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-brown" />
                        <Input value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} type="password" placeholder="Your password"
                          className="pl-10 bg-cream/50 border-border-custom" data-testid="login-password" required />
                      </div>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-rust hover:bg-rust/90 text-cream font-body rounded-full h-11" data-testid="login-submit-btn">
                      {loading ? <span className="spinner mr-2"></span> : null} Sign In
                    </Button>
                    <div className="text-center mt-2">
                       <button type="button" onClick={() => { setViewMode('forgot'); setError(''); }} className="font-body text-sm text-rust hover:underline">
                         Forgot your password?
                       </button>
                    </div>
                  </div>
                </form>
              </TabsContent>

              {/* ── Sign Up ── */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} data-testid="register-form">
                  <div className="space-y-4">
                    <div>
                      <Label className="font-body text-sm text-dark-brown">Full Name</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-brown" />
                        <Input value={regName} onChange={(e) => setRegName(e.target.value)} placeholder="Your name"
                          className="pl-10 bg-cream/50 border-border-custom" data-testid="register-name" required />
                      </div>
                    </div>
                    <div>
                      <Label className="font-body text-sm text-dark-brown">Email <span className="text-red-500">*</span></Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-brown" />
                        <Input value={regEmail} onChange={(e) => setRegEmail(e.target.value)} type="email" placeholder="you@example.com"
                          className="pl-10 bg-cream/50 border-border-custom" data-testid="register-email" required />
                      </div>
                    </div>
                    <div>
                      <Label className="font-body text-sm text-dark-brown">Password</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-brown" />
                        <Input value={regPassword} onChange={(e) => setRegPassword(e.target.value)} type="password" placeholder="Min 6 characters"
                          className="pl-10 bg-cream/50 border-border-custom" data-testid="register-password" required />
                      </div>
                    </div>
                    <div>
                      <Label className="font-body text-sm text-dark-brown">Mobile <span className="text-red-500">*</span></Label>
                      <div className="flex mt-1">
                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-cream text-mid-brown font-body text-sm">+91</span>
                        <Input
                          value={regPhone}
                          onChange={handlePhoneChange}
                          placeholder="10-digit mobile"
                          inputMode="numeric"
                          maxLength={10}
                          required
                          className={`rounded-l-none bg-cream/50 border-border-custom ${regPhoneError ? 'border-red-400' : (regPhone.length === 10 && !regPhoneError) ? 'border-green-400' : ''}`}
                          data-testid="register-phone"
                        />
                      </div>
                      {regPhoneError && <p className="font-body text-xs text-red-500 mt-1">{regPhoneError}</p>}
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-rust hover:bg-rust/90 text-cream font-body rounded-full h-11" data-testid="register-submit-btn">
                      {loading ? <span className="spinner mr-2"></span> : <Mail className="w-4 h-4 mr-2" />}
                      Create Account & Send OTP
                    </Button>
                    <p className="font-body text-xs text-center text-light-brown">
                      A verification OTP will be emailed to you
                    </p>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
    </div>
  );
}
